const ORGANIZATION_PATH = /^\/organizations\/(\d+)(?=\/|$|\?)/;

type Listener = (organizationId: number | null) => void;
type AccessLostListener = (organizationId: number) => void;

let suspendedOrganizationId: number | null = null;
const listeners = new Set<Listener>();
const accessLostListeners = new Set<AccessLostListener>();

export function organizationIdFromApiPath(path: string): number | null {
  const match = ORGANIZATION_PATH.exec(path);
  if (!match) return null;
  const organizationId = Number(match[1]);
  return Number.isInteger(organizationId) ? organizationId : null;
}

/**
 * Records an explicit ORGANIZATION_SUSPENDED or ORGANIZATION_ACCESS_DENIED
 * response for an organization path.
 */
export function noteOrganizationRequestFailure(path: string, code: string): void {
  if (code === "ORGANIZATION_ACCESS_DENIED") {
    const organizationId = organizationIdFromApiPath(path);
    if (organizationId === null) return;
    for (const listener of accessLostListeners) listener(organizationId);
    return;
  }
  if (code !== "ORGANIZATION_SUSPENDED") return;
  const organizationId = organizationIdFromApiPath(path);
  if (organizationId === null || suspendedOrganizationId === organizationId) return;
  suspendedOrganizationId = organizationId;
  for (const listener of listeners) listener(organizationId);
}

export function currentSuspendedOrganizationId(): number | null {
  return suspendedOrganizationId;
}

export function subscribeOrganizationSuspended(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Membership removed, Platform Admin revoked, or organization deleted. */
export function subscribeOrganizationAccessLost(listener: AccessLostListener): () => void {
  accessLostListeners.add(listener);
  return () => {
    accessLostListeners.delete(listener);
  };
}

export function clearOrganizationSuspended(): void {
  if (suspendedOrganizationId === null) return;
  suspendedOrganizationId = null;
  for (const listener of listeners) listener(null);
}

export function resetOrganizationSuspendedForTests(): void {
  suspendedOrganizationId = null;
  listeners.clear();
  accessLostListeners.clear();
}
